
import React, { useState, useEffect } from 'react';
import { updatePlatformSettings } from '../services/apiService';
import { testSmtpConnection, sendTestEmail } from '../services/apiService';
import { LoadingSpinner, LogoPlaceholder } from './icons';
import { PlatformSettings, User } from '../types';

type SettingsTab = 'branding' | 'notifications' | 'legal' | 'backend';

const getCurrentUserFromSession = (): User | null => {
    const storedUser = sessionStorage.getItem('currentUser');
    return storedUser ? JSON.parse(storedUser) : null;
}

interface SettingsPageProps {
    platformSettings: PlatformSettings | null;
    onSettingsUpdate: (settings: PlatformSettings) => void;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ platformSettings, onSettingsUpdate }) => {
    const [settings, setSettings] = useState<PlatformSettings | null>(platformSettings);
    const [isLoading, setIsLoading] = useState(!platformSettings);
    const [isSaving, setIsSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<SettingsTab>('branding');
    
    // Testing states
    const [isTestingSmtp, setIsTestingSmtp] = useState(false);
    const [smtpTestResult, setSmtpTestResult] = useState<{success: boolean, message: string} | null>(null);
    const [testEmailAddress, setTestEmailAddress] = useState('');
    const [isSendingTest, setIsSendingTest] = useState(false);
    const [emailTestResult, setEmailTestResult] = useState<{success: boolean, message: string} | null>(null);

    // DB Config state for the Backend tab
    const [dbConfig, setDbConfig] = useState({
        host: 'localhost',
        dbname: 'tcr_portal_db',
        user: 'tcr_user',
        pass: ''
    });

    useEffect(() => {
        if (platformSettings) {
            setSettings(platformSettings);
            setIsLoading(false);
        }
    }, [platformSettings]);

    const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        
        if (name === 'privacyPolicy' || name === 'termsAndConditions') {
            setSettings(prev => prev ? ({ 
                ...prev, 
                legalContent: { ...prev.legalContent, [name]: value }
            }) : null);
        } else if (name === 'smtpPort') {
            setSettings(prev => prev ? ({ ...prev, [name]: parseInt(value, 10) }) : null);
        } else {
            const checked = (e.target as HTMLInputElement).checked;
            setSettings(prev => prev ? ({ ...prev, [name]: type === 'checkbox' ? checked : value }) : null);
        }
    };

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'platformLogo' | 'loginPageLogo') => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSettings(prev => prev ? ({ ...prev, [field]: reader.result as string }) : null);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        const currentUser = getCurrentUserFromSession();
        if (!settings || !currentUser) return;
        setIsSaving(true);
        try {
            // If password field is empty string, don't send it to preserve existing
            const settingsToSave = { ...settings };
            if (settingsToSave.smtpPassword === "") delete settingsToSave.smtpPassword;
            
            const updatedSettings = await updatePlatformSettings(settingsToSave);
            onSettingsUpdate(updatedSettings);
            alert("Settings saved successfully!");
        } catch(e) {
            alert("Failed to save settings.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleTestSmtp = async () => {
        if (!settings) return;
        setIsTestingSmtp(true);
        setSmtpTestResult(null);
        try {
            const res = await testSmtpConnection(settings);
            setSmtpTestResult({ success: true, message: res });
        } catch (err) {
            setSmtpTestResult({ success: false, message: (err as Error).message });
        } finally {
            setIsTestingSmtp(false);
        }
    };

    const handleSendTestEmail = async () => {
        if (!settings || !testEmailAddress) {
            setEmailTestResult({ success: false, message: "Please enter a recipient email address." });
            return;
        }
        setIsSendingTest(true);
        setEmailTestResult(null);
        try {
            const res = await sendTestEmail(testEmailAddress, settings);
            setEmailTestResult({ success: true, message: res });
        } catch (err) {
            setEmailTestResult({ success: false, message: (err as Error).message });
        } finally {
            setIsSendingTest(false);
        }
    };

    const generatePhpConfig = () => {
        const code = `<?php
$db_config = [
    'host' => '${dbConfig.host}',
    'dbname' => '${dbConfig.dbname}',
    'user' => '${dbConfig.user}',
    'pass' => '${dbConfig.pass}'
];
?>`;
        const blob = new Blob([code], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'config.php';
        a.click();
    };

    const TabButton: React.FC<{tab: SettingsTab, label: string}> = ({tab, label}) => (
        <button
            onClick={() => setActiveTab(tab)}
            className={`${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
        >
            {label}
        </button>
    );

  if (isLoading || !settings) {
    return <div className="flex justify-center items-center h-full"><LoadingSpinner className="w-10 h-10 text-primary" /></div>;
  }

  return (
    <div className="bg-white p-4 sm:p-8 rounded-lg shadow-md max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-2">Platform Settings</h1>
      <p className="text-gray-500 mb-6 text-sm">This page is only visible to Super Admins.</p>
      
      <div className="border-b border-gray-200 mb-8 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 whitespace-nowrap" aria-label="Tabs">
          <TabButton tab="branding" label="Branding & Appearance" />
          <TabButton tab="notifications" label="Notifications & Email" />
          <TabButton tab="legal" label="Legal" />
          <TabButton tab="backend" label="Backend & Database" />
        </nav>
      </div>

      <div className="min-h-[400px]">
        {activeTab === 'branding' && (
            <div className="space-y-10">
                <section>
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Dashboard Branding & Appearance</h2>
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Platform Name</label>
                            <input type="text" name="platformName" value={settings.platformName} onChange={handleSettingsChange} className="block w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Platform Logo (for Dashboard)</label>
                            <div className="flex items-center space-x-4">
                                <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded border flex items-center justify-center text-gray-300 overflow-hidden">
                                    {settings.platformLogo ? <img src={settings.platformLogo} className="h-full w-full object-contain" /> : <LogoPlaceholder className="w-8 h-8" />}
                                </div>
                                <input type="file" onChange={(e) => handleLogoUpload(e, 'platformLogo')} className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-primary hover:file:bg-blue-100 cursor-pointer" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text</label>
                            <input type="text" name="footerText" value={settings.footerText} onChange={handleSettingsChange} className="block w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"/>
                        </div>
                    </div>
                </section>

                <section className="border-t pt-10">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Login Page Appearance</h2>
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Login Page Title</label>
                            <input type="text" name="loginPageTitle" value={settings.loginPageTitle} onChange={handleSettingsChange} className="block w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Login Page Logo</label>
                            <div className="flex items-center space-x-4">
                                <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded border flex items-center justify-center text-gray-300 overflow-hidden">
                                    {settings.loginPageLogo ? <img src={settings.loginPageLogo} className="h-full w-full object-contain" /> : <LogoPlaceholder className="w-8 h-8" />}
                                </div>
                                <input type="file" onChange={(e) => handleLogoUpload(e, 'loginPageLogo')} className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-primary hover:file:bg-blue-100 cursor-pointer" />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        )}

        {activeTab === 'notifications' && (
            <div className="space-y-8">
                <section>
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Notification Settings</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Admin Email for Notifications</label>
                            <input type="email" name="adminEmail" value={settings.adminEmail} onChange={handleSettingsChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"/>
                            <p className="mt-1 text-xs text-gray-500">This email will receive system-wide alerts.</p>
                        </div>
                        <div className="flex items-center">
                            <input type="checkbox" id="notifyOnNewIncident" name="notifyOnNewIncident" checked={settings.notifyOnNewIncident} onChange={handleSettingsChange} className="h-4 w-4 text-primary border-gray-300 rounded" />
                            <label htmlFor="notifyOnNewIncident" className="ml-2 block text-sm text-gray-900">Notify on new incident reports</label>
                        </div>
                        <div className="flex items-center">
                            <input type="checkbox" id="notifyOnStatusChange" name="notifyOnStatusChange" checked={settings.notifyOnStatusChange} onChange={handleSettingsChange} className="h-4 w-4 text-primary border-gray-300 rounded" />
                            <label htmlFor="notifyOnStatusChange" className="ml-2 block text-sm text-gray-900">Notify on status changes</label>
                        </div>
                    </div>
                </section>

                <section className="border-t pt-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">SMTP Outgoing Email Server</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Host</label>
                            <input type="text" name="smtpHost" value={settings.smtpHost} onChange={handleSettingsChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Port</label>
                            <input type="number" name="smtpPort" value={settings.smtpPort} onChange={handleSettingsChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Username</label>
                            <input type="text" name="smtpUsername" value={settings.smtpUsername} onChange={handleSettingsChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Password</label>
                            <input type="password" name="smtpPassword" placeholder="••••••••" onChange={handleSettingsChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Encryption</label>
                            <select name="smtpEncryption" value={settings.smtpEncryption} onChange={handleSettingsChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md">
                                <option value="none">None</option>
                                <option value="ssl">SSL</option>
                                <option value="tls">TLS</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">From Address</label>
                            <input type="email" name="smtpFromAddress" value={settings.smtpFromAddress} onChange={handleSettingsChange} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                        <div className="flex-grow">
                            <button onClick={handleTestSmtp} disabled={isTestingSmtp} className="flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm hover:bg-gray-50">
                                {isTestingSmtp && <LoadingSpinner className="w-4 h-4 mr-2"/>} Test Connection
                            </button>
                            {smtpTestResult && <p className={`mt-2 text-xs ${smtpTestResult.success ? 'text-green-600' : 'text-red-600'}`}>{smtpTestResult.message}</p>}
                        </div>
                        <div className="flex-grow space-y-2">
                            <div className="flex gap-2">
                                <input type="email" placeholder="Test recipient email..." value={testEmailAddress} onChange={e => setTestEmailAddress(e.target.value)} className="p-2 border rounded-md text-sm flex-grow" />
                                <button onClick={handleSendTestEmail} disabled={isSendingTest} className="px-4 py-2 bg-secondary text-white rounded-md text-sm hover:bg-gray-800">
                                    Send Test Email
                                </button>
                            </div>
                            {emailTestResult && <p className={`text-xs ${emailTestResult.success ? 'text-green-600' : 'text-red-600'}`}>{emailTestResult.message}</p>}
                        </div>
                    </div>
                </section>
            </div>
        )}

        {activeTab === 'legal' && (
            <div className="space-y-8">
                <h2 className="text-xl font-bold text-gray-800">Legal Documents</h2>
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Policy</label>
                        <textarea name="privacyPolicy" value={settings.legalContent.privacyPolicy} onChange={handleSettingsChange} rows={10} className="block w-full p-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary font-mono text-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Terms and Conditions</label>
                        <textarea name="termsAndConditions" value={settings.legalContent.termsAndConditions} onChange={handleSettingsChange} rows={10} className="block w-full p-3 border border-gray-300 rounded-md focus:ring-primary focus:border-primary font-mono text-sm" />
                    </div>
                </div>
            </div>
        )}

        {activeTab === 'backend' && (
            <div className="space-y-8">
                <section>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Production MySQL Database Setup</h2>
                    <p className="text-sm text-gray-500 mb-6">Connect the portal to your private MySQL database.</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Database Host</label>
                            <input type="text" value={dbConfig.host} onChange={e => setDbConfig({...dbConfig, host: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Database Name</label>
                            <input type="text" value={dbConfig.dbname} onChange={e => setDbConfig({...dbConfig, dbname: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Database Username</label>
                            <input type="text" value={dbConfig.user} onChange={e => setDbConfig({...dbConfig, user: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Database Password</label>
                            <input type="password" value={dbConfig.pass} onChange={e => setDbConfig({...dbConfig, pass: e.target.value})} className="mt-1 block w-full p-2 border border-gray-300 rounded-md" />
                        </div>
                    </div>

                    <div className="mt-10 p-6 bg-blue-50 rounded-lg border border-blue-100">
                        <h3 className="font-bold text-blue-900 mb-2">Setup Steps:</h3>
                        <ol className="list-decimal list-inside text-sm text-blue-800 space-y-2">
                            <li>Ensure your server runs PHP 7.4+.</li>
                            <li>Import <strong>schema.sql</strong> into your MySQL database.</li>
                            <li>Upload <strong>api.php</strong> to your web root.</li>
                            <li>Click below to download <strong>config.php</strong> and place it next to api.php.</li>
                        </ol>
                        <button onClick={generatePhpConfig} className="mt-6 px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark">
                            Download config.php Snippet
                        </button>
                    </div>
                </section>
            </div>
        )}
      </div>

      <div className="mt-12 border-t pt-8 flex justify-end">
          <button 
              onClick={handleSave}
              disabled={isSaving}
              className="px-8 py-3 bg-primary text-white font-bold rounded-md hover:bg-primary-light disabled:bg-gray-400 shadow-lg transition-all"
          >
              {isSaving ? <LoadingSpinner className="w-5 h-5 mx-auto"/> : 'Save All Settings'}
          </button>
      </div>
    </div>
  );
};

export default SettingsPage;
